import { useCallback, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "../../../lib/http";
import { useTokenGetter } from "../../auth/hooks/useTokenGetter";
import {
  NO_REFUND_OFFER,
  fetchRefundOffer,
  requestRefund,
} from "../services/refundService";
import type {
  RefundOffer,
  RefundRequestRecord,
} from "../types/subscriptions.types";

export type RefundOfferStatus = "loading" | "ready" | "failed";

export interface RefundOfferState {
  /**
   * The whole panel: may they ask, what would go back, when it shuts, what the
   * dropdown holds, and what became of a request already made.
   *
   * The closed offer until the answer arrives — never `null`, so no screen has
   * to guard the shape before reading it. `status` is what says whether the
   * closure is the server's answer or simply the absence of one, and the foot
   * of the Plan details screen draws nothing until it is `ready`.
   */
  offer: RefundOffer;
  status: RefundOfferStatus;
  error?: string;
  reload: () => void;
  /**
   * Asks for the money back, with the reason where one was picked.
   *
   * Answers with the record the backend took, and folds it into `offer` — so
   * the screen redraws into the refund states from what the server did rather
   * than from what this hook hoped it would do.
   */
  request: (reasonCode?: string | null) => Promise<RefundRequestRecord | null>;
}

export const REFUND_OFFER_QUERY_KEY = ["billing", "refund-offer"] as const;

/**
 * The refund panel, from `GET /api/v1/billing/refund/`.
 *
 * One call answers all of it, which is the point: every part is a claim about
 * money, and the four the browser used to make on its own — reading the invoice
 * list for a first purchase, counting seven days against the laptop's clock,
 * quoting the newest invoice row as the payment, and offering reasons compiled
 * into the bundle — could each disagree with the server. See `refundService`.
 *
 * A workspace with nothing to refund is `ready` and not eligible, which is an
 * ordinary answer and not a failure: it is the state of everybody who has never
 * subscribed, and of every subscriber past their first week.
 *
 * Backed by TanStack Query: a screen visited again shows the last-read panel
 * immediately while a fresh read checks it behind the scenes, so a refund
 * asked for on another device still catches up within moments. The window
 * itself still shuts on the server's clock regardless of what this cache
 * shows — a stale `eligible: true` past the deadline would fail the request
 * the same way asking a moment too late always has.
 *
 * Must be used inside a Clerk provider and an active session.
 */
export function useRefundOffer(): RefundOfferState {
  const getToken = useTokenGetter();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: REFUND_OFFER_QUERY_KEY,
    queryFn: ({ signal }) => fetchRefundOffer(getToken, signal),
  });

  useEffect(() => {
    if (query.isError) {
      console.error("refund offer failed to load:", query.error);
    }
  }, [query.isError, query.error]);

  const request = useCallback(
    async (reasonCode?: string | null) => {
      const record = await requestRefund(getToken, reasonCode);

      // The offer is spent the moment it is taken, and the request that
      // replaces it is what the banner is drawn from. Both in one update, so
      // there is no frame where the card still offers what has just been asked
      // for.
      queryClient.setQueryData<RefundOffer>(
        REFUND_OFFER_QUERY_KEY,
        (current: RefundOffer | undefined) => ({
          ...(current ?? NO_REFUND_OFFER),
          eligible: false,
          request: record ?? current?.request ?? null,
        })
      );

      return record;
    },
    [getToken, queryClient]
  );

  return {
    offer: query.data ?? NO_REFUND_OFFER,
    status:
      query.status === "pending"
        ? "loading"
        : query.status === "error"
        ? "failed"
        : "ready",
    error:
      query.isError && query.error instanceof ApiError
        ? query.error.message
        : query.isError
        ? "We could not reach the server. Check your connection and try again."
        : undefined,
    reload: () => {
      void query.refetch();
    },
    request,
  };
}

export default useRefundOffer;
